/**
 * Copyright 2024-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */
import { useEffect, useMemo, useReducer } from "react";
import dayjs from "dayjs";
import { When } from "react-if";
import { Button, theme } from "@nordcloud/gnui";
import { GroupTypes, GroupsSortFields, SortOrder } from "~/generated/graphql";
import { Mode, Sidebar } from "~/components";
import { FilterSidebarFooter } from "~/components/SidebarWithMultiSelect/FilterSidebarFooter";
import { FilterWrapper } from "~/components/SidebarWithMultiSelect/FilterWrapper";
import { SelectableDateRange } from "~/components/SidebarWithMultiSelect/SelectableDateRange";
import { SelectableListWithSearch } from "~/components/SidebarWithMultiSelect/SelectableListWithSearch";
import { dateFormat } from "~/constants";
import { useDisclosure, useQueryState } from "~/hooks";
import {
  getFilterSidebarState,
  getLivePreviewFilterSidebarFooterState,
  hasFilterChanges,
  isNotEmpty,
  isNotNil,
} from "~/tools";
import { resourceGroupsExcludeFilterMap } from "~/tools/filterStateMaps";
import { useGetResourceGroups } from "~/views/resourceGroups/hooks";
import { ResourceGroupQuery } from "~/views/resourceGroups/types";
import { GROUP_TYPE_LABELS } from "../constants";
import { prepareResourceGroupsFilter } from "./resourceGroupFilterBuilders";

type State = {
  startDate?: string;
  endDate?: string;
  types: GroupTypes[];
  excludeTypes: boolean;
};
type ResourceGroupQueryState = ResourceGroupQuery & {
  page?: string | number;
  limit?: string | number;
};

const groupTypeCheckboxes = Object.values(GroupTypes).map((type) => ({
  value: type,
  label: GROUP_TYPE_LABELS[type],
}));

function buildState(queryState: ResourceGroupQuery) {
  return {
    startDate: queryState.startDateUTC
      ? dayjs(queryState.startDateUTC).format(dateFormat.shortDate)
      : undefined,
    endDate: queryState.endDateUTC
      ? dayjs(queryState.endDateUTC).format(dateFormat.shortDate)
      : undefined,
    types: queryState.types ?? [],
    excludeTypes: queryState.excludeTypes ?? false,
  };
}

function buildQueryStateFromSidebarState(
  queryState: ResourceGroupQueryState,
  state: State
): ResourceGroupQueryState {
  return {
    ...queryState,
    startDateUTC: state.startDate
      ? dayjs(state.startDate).format(dateFormat.fullDate)
      : undefined,
    endDateUTC: state.endDate
      ? dayjs(state.endDate).endOf("day").format(dateFormat.fullDate)
      : undefined,
    types: state.types,
    excludeTypes: state.excludeTypes,
  };
}

export function ResourceGroupFiltersSidebar({
  resultsCount = 0,
}: {
  resultsCount?: number;
}) {
  const { isOpen, open, close } = useDisclosure();
  const { queryState, updateQueryState } = useQueryState<ResourceGroupQuery>();

  const [state, updateState] = useReducer(
    (data: State, partialData: Partial<State>): State => ({
      ...data,
      ...partialData,
    }),
    buildState(queryState)
  );

  useEffect(() => {
    updateState(buildState(queryState));
  }, [queryState, isOpen]);

  const appliedState = buildState(queryState);
  const hasPendingChanges = hasFilterChanges(
    state,
    appliedState,
    resourceGroupsExcludeFilterMap
  );
  const draftQueryState = useMemo(
    () => buildQueryStateFromSidebarState(queryState, state),
    [queryState, state]
  );
  const { count: previewCount, loading: previewLoading } = useGetResourceGroups(
    {
      page: String(draftQueryState.page ?? "1"),
      limit: String(draftQueryState.limit ?? "20"),
      sort: {
        field: draftQueryState.field ?? GroupsSortFields.Name,
        order: draftQueryState.order ?? SortOrder.Asc,
      },
      filter: prepareResourceGroupsFilter(draftQueryState),
      basic: true,
      skip: !isOpen || !hasPendingChanges,
    }
  );
  const previewResultsCount = previewLoading ? undefined : previewCount;
  const previewFooterState = getLivePreviewFilterSidebarFooterState({
    hasPendingChanges,
    isOpen,
    previewLoading,
    previewResultsCount,
    appliedResultsCount: resultsCount,
    entityLabel: "resource groups",
  });

  const applyFilters = () => {
    const nextQueryState = buildQueryStateFromSidebarState(queryState, state);
    updateQueryState({
      ...nextQueryState,
      limit: String(nextQueryState.limit ?? queryState.limit ?? "20"),
      page: "1",
    });
    close();
  };

  const clearAllFilters = () => {
    updateState({
      startDate: undefined,
      endDate: undefined,
      types: [],
      excludeTypes: false,
    });
    updateQueryState({
      ...queryState,
      startDateUTC: undefined,
      endDateUTC: undefined,
      types: [],
      excludeTypes: false,
      page: "1",
    });
    close();
  };

  const clearDateCreatedFilter = () => {
    updateState({ startDate: undefined, endDate: undefined });
  };

  const clearTypesFilter = () => {
    updateState({
      types: [],
      excludeTypes: false,
    });
  };
  const filterSidebarState = getFilterSidebarState({
    currentState: state,
    appliedState,
    resultsCount: previewFooterState.effectiveResultsCount,
    entityLabel: "resource groups",
    excludeFilterMap: resourceGroupsExcludeFilterMap,
  });
  const helperText =
    previewFooterState.helperTextOverride ?? filterSidebarState.helperText;

  return (
    <>
      <Button
        mr={theme.spacing.spacing04}
        severity="low"
        icon="filter"
        onClick={open}
      >
        Filters
      </Button>
      <When condition={isOpen}>
        <Sidebar
          title="Filter Results"
          isOpen={isOpen}
          onClick={close}
          footer={
            <FilterSidebarFooter
              helperText={helperText}
              isApplyDisabled={filterSidebarState.isApplyDisabled}
              isClearDisabled={filterSidebarState.isClearDisabled}
              onApply={applyFilters}
              onClear={clearAllFilters}
            />
          }
        >
          <FilterWrapper
            name="Creation Date"
            isSelected={!!state?.startDate && !!state?.endDate}
            selectedOptions={["startDate", "endDate"]}
            onClear={clearDateCreatedFilter}
          >
            <SelectableDateRange
              selectedRange={{
                startDate: state.startDate,
                endDate: state.endDate,
              }}
              onRangeSelect={(range) => updateState(range)}
              label="Time range when group was created"
              setDefaultEndDateToToday
            />
          </FilterWrapper>
          <FilterWrapper
            name="Type"
            isSelected={isNotNil(state.types) && isNotEmpty(state.types)}
            selectedOptions={state.types}
            onClear={clearTypesFilter}
          >
            <SelectableListWithSearch
              allowModeChange
              selectedOptions={state.types}
              selectableItems={groupTypeCheckboxes}
              isExcludeSelected={state.excludeTypes}
              onMultiSelect={(types) =>
                updateState({ types: types as GroupTypes[] })
              }
              onModeChange={(mode) =>
                updateState({ excludeTypes: mode === Mode.EXCLUDES })
              }
            />
          </FilterWrapper>
        </Sidebar>
      </When>
    </>
  );
}
